import React from "react";
import {AbsoluteFill, Sequence, staticFile} from "remotion";
import {VideoClip} from "../../components/media/VideoClip";
import {JumpCut} from "../../components/media/JumpCut";
import {CaptionOverlay, type CaptionPreset} from "../../components/text/CaptionOverlay";
import {AnimatedTitle} from "../../components/text/AnimatedTitle";
import {LowerThird} from "../../components/text/LowerThird";
import {CallToAction} from "../../components/overlays/CallToAction";
import {ProgressBar} from "../../components/overlays/ProgressBar";
import {AudioTrack} from "../../components/media/AudioTrack";
import {useSilenceSegments} from "../../hooks/useSilenceSegments";
import {buildCutList, mergeSegments} from "../../utils/editing";
import {Watermark} from "../../components/overlays/Watermark";
import {loadDefaultFonts} from "../../presets/fonts";
import {BRAND} from "../../presets/brand";
import {OrganicLayer} from "../../components/overlays/OrganicLayer";
import {FilmGrain as CinemaGrain, CinemaVignette} from "../../components/overlays/FilmGrain";

export interface TalkingHeadEditProps {
  videoSrc?: string;
  captionsPath?: string;
  silencePath?: string;
  removeSilence?: boolean;
  showCaptions?: boolean;
  captionPreset?: CaptionPreset;
  title?: string;
  titleDuration?: number;
  speakerName?: string;
  speakerTitle?: string;
  ctaText?: string;
  ctaDuration?: number;
  backgroundMusic?: string;
  musicVolume?: number;
  accentColor?: string;
}

export const TalkingHeadEdit: React.FC<TalkingHeadEditProps> = ({
  videoSrc = "assets/video.mp4",
  captionsPath,
  silencePath,
  removeSilence = false,
  showCaptions = true,
  captionPreset = "bold",
  title,
  titleDuration = 90,
  speakerName,
  speakerTitle,
  ctaText,
  ctaDuration = 90,
  backgroundMusic,
  musicVolume = 0.15,
  accentColor = "#6366f1",
}) => {
  loadDefaultFonts();

  const silenceData = useSilenceSegments(silencePath ?? null);

  // Build cut list from speech segments
  const segments = silenceData
    ? mergeSegments(
        buildCutList(silenceData.speechSegments, {paddingSeconds: 0.15}),
        0.3,
      )
    : [];

  const src = staticFile(videoSrc);

  return (
    <OrganicLayer intensity="subtle" seed="talkinghead">
    <AbsoluteFill>
      {/* Video layer — either jump-cut or full */}
      {removeSilence && segments.length > 0 ? (
        <JumpCut src={src} segments={segments} paddingSeconds={0} />
      ) : (
        <VideoClip src={src} />
      )}

      {/* Captions */}
      {showCaptions && captionsPath && (
        <CaptionOverlay
          captionsSource={captionsPath}
          preset={captionPreset}
          position="bottom"
        />
      )}

      {/* Title overlay at start */}
      {title && (
        <Sequence from={0} durationInFrames={titleDuration}>
          <AbsoluteFill style={{justifyContent: "center", alignItems: "center"}}>
            <AnimatedTitle
              text={title}
              fontSize={56}
              fontWeight={800}
              color="#ffffff"
              enterAnimation="scale"
              exitAnimation="fade"
              enterDuration={20}
              holdDuration={titleDuration - 40}
              exitDuration={15}
              textShadow={`0 4px 20px ${accentColor}40`}
            />
          </AbsoluteFill>
        </Sequence>
      )}

      {/* Lower third */}
      {speakerName && (
        <Sequence from={title ? titleDuration - 10 : 15} durationInFrames={150}>
          <LowerThird
            name={speakerName}
            title={speakerTitle}
            accentColor={accentColor}
            enterDuration={20}
            holdDuration={90}
            exitDuration={15}
          />
        </Sequence>
      )}

      {/* CTA at the end */}
      {ctaText && (
        <Sequence from={0}>
          <CallToAction
            text={ctaText}
            backgroundColor={accentColor}
            enterDelay={Math.max(0, (removeSilence && segments.length > 0
              ? segments.reduce((sum, s) => sum + (s.endSeconds - s.startSeconds), 0) * 30
              : 200) - ctaDuration)}
          />
        </Sequence>
      )}

      {/* Background music */}
      {backgroundMusic && (
        <AudioTrack
          src={staticFile(backgroundMusic)}
          volume={musicVolume}
          fadeInDurationSeconds={2}
          fadeOutDurationSeconds={3}
          loop
        />
      )}

      {/* Progress bar */}
      <ProgressBar color={accentColor} height={3} />

      {/* Brand watermark */}
      <Watermark text={BRAND.handle} corner="topRight" opacity={0.5} fontSize={14} color="#ffffff" margin={30} />
      <CinemaGrain opacity={0.06} />
      <CinemaVignette opacity={0.5} />
    </AbsoluteFill>
    </OrganicLayer>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
